import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import './AddCourseDialog.css';

const EditCourseDialog = ({ course, isOpen, onClose, onSave, faculties = [] }) => {
  const [formData, setFormData] = useState({
    course_name: '',
    course_code: '',
    credit: '',
    faculty_name: '',
    faculty_id: '',
    semester: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (course && isOpen) {
      setFormData({
        course_name: course.course_name || '',
        course_code: course.course_code || '',
        credit: course.credit || '',
        faculty_name: course.faculty_name || '',
        faculty_id: course.faculty_id || '',
        semester: course.semester || ''
      });
      setError('');
    }
  }, [course, isOpen]);

    const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'faculty_name' && value) {
      const selectedFaculty = faculties.find(f => f.name === value);
      if (selectedFaculty) {
        setFormData(prev => ({ ...prev, faculty_name: value, faculty_id: selectedFaculty._id }));
        return;
      }
    }
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!formData.course_name.trim() || !formData.course_code.trim() || !formData.credit) {
      setError('Please fill in all required fields.');
      setLoading(false);
      return;
    }

    const semesterLabels = {
      '1st Semester': '1st Semester', '2nd Semester': '2nd Semester',
      '3rd Semester': '3rd Semester', '4th Semester': '4th Semester',
      '5th Semester': '5th Semester', '6th Semester': '6th Semester',
      '7th Semester': '7th Semester', '8th Semester': '8th Semester'
    };

    const submitData = {
      course_name: formData.course_name.trim(),
      course_code: formData.course_code.trim(),
      credit: parseInt(formData.credit, 10),
      faculty_name: formData.faculty_name,
      faculty_id: formData.faculty_id,
      semester: semesterLabels[formData.semester] || formData.semester
    };

    try {
      const response = await api.put(`/courses/${course._id}`, submitData);
      if (response.data.success) {
        onSave(response.data.data);
        onClose();
      } else {
        setError(response.data.message || 'Failed to update course');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update course');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen || !course) return null;

  return (
    <div className="dialog-overlay" onClick={handleClose}>
      <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>Edit Course</h2>
          <button className="close-button" onClick={handleClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="dialog-form">
          {error && <div className="error-message">{error}</div>}

          {faculties.length > 0 && (
            <div className="form-group">
              <label htmlFor="faculty_name">Faculty</label>
              <select
                id="faculty_name"
                name="faculty_name"
                value={formData.faculty_name}
                onChange={handleChange}
                disabled={loading}
              >
                <option value="">Select faculty</option>
                {faculties.map((faculty) => (
                  <option key={faculty._id} value={faculty.name}>
                    {faculty.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="semester">Semester</label>
            <select
              id="semester"
              name="semester"
              value={formData.semester}
              onChange={handleChange}
              disabled={loading}
            >
              <option value="">Select semester</option>
              <option value="1st Semester">1st Semester</option>
              <option value="2nd Semester">2nd Semester</option>
              <option value="3rd Semester">3rd Semester</option>
              <option value="4th Semester">4th Semester</option>
              <option value="5th Semester">5th Semester</option>
              <option value="6th Semester">6th Semester</option>
              <option value="7th Semester">7th Semester</option>
              <option value="8th Semester">8th Semester</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="course_name">Course Name *</label>
            <input
              type="text"
              id="course_name"
              name="course_name"
              value={formData.course_name}
              onChange={handleChange}
              required
              placeholder="Enter course name"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="course_code">Course Code *</label>
            <input
              type="text"
              id="course_code"
              name="course_code"
              value={formData.course_code}
              onChange={handleChange}
              required
              placeholder="Enter course code (e.g., CSE101)"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="credit">Credit Hours *</label>
            <input
              type="number"
              id="credit"
              name="credit"
              value={formData.credit}
              onChange={handleChange}
              required
              min="1"
              max="6"
              placeholder="Enter credit hours (1-6)"
              disabled={loading}
            />
          </div>

          <div className="dialog-actions">
            <button type="button" onClick={handleClose} className="cancel-button" disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="save-button" disabled={loading}>
              {loading ? 'Updating...' : 'Update Course'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCourseDialog;
